import React, {useEffect} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchBox from '../Components/Search/SearchBox';
import { parseAutocompleteConfig } from '../Components/Search/autocompleteConfig';
import { useLocalization } from '../Components/Helpers/useLocalization';
import {
    useAddBundleToCartMutation,
    useAddSavedSearchToCartMutation,
    useAddToCartMutation, useGetCategoriesQuery,
    useGetStorefrontQuery,
    useSearchProductsQuery
} from '../Api/Api';
import {
    InputOnChangeData,
    Button, MessageBar, MessageBarBody, MessageBarTitle, MessageBarActions, Spinner,
    Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions, Tooltip
} from '@fluentui/react-components';
import CAN, {PRICING, CART_MANAGEMENT} from "../Components/Auth/Permissions";

import {CartRegular, DismissRegular, ProhibitedRegular, NewFilled, FilterRegular, TagRegular, PanelLeftContractRegular, PanelLeftExpandRegular} from '@fluentui/react-icons';
import CurrencyFormat from '../Components/Helpers/CurrencyFormat';
import { IProduct, ISearchContext } from '../Api/Models/Products';
import AddedToCartDialog from '../Components/Products/AddedToCartDialog';
import { ProductImage } from "../Components/Helpers/ProductImage";
import Paginator from "../Components/Paginator";
import {
    useAddBundleToPreparedCartMutation,
    useAddToPreparedCartMutation,
    useGetPreparedCartQuery
} from "../Api/PreparedCartEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../Store/Store";
import {IActionResponse} from "../Api/Models/ActionResponse";
import {IBundleSelectionItem} from "../Api/Models/Cart";
import {useStartNewCart} from "../Components/Cart/useStartNewCart";
import ProductCard from "../Components/Cards/ProductCard";
import PriceQuantityNotice from "../Components/Product/PriceQuantityNotice";
import StockAvailabilityNotice from "../Components/Product/StockAvailabilityNotice";
import {parseSearchPageConfig, cardStyleFrom, textStyleFrom, priceStyleFrom, containerStyleFrom, elementStyleFrom, importantStyleRef, importantStyleCss, FEATURED_TAG} from "../Components/Helpers/searchPageConfig";
import {hoverPartFor, resolveHoverPart} from "../Components/Helpers/stylesConfig";
import {parseHeaderConfig} from "../Components/Helpers/headerFooterConfig";
import {useFitsViewport} from "../Components/Helpers/useFitsViewport";
import FavoriteButton from "../Components/Product/FavoriteButton";
import QuotePrice from "../Components/Product/QuotePrice";
import {isDarkColor, tagColor} from "../Components/Helpers/ColorHelpers";
import FacetFilters from "../Components/Search/FacetFilters";

// Remembered-filters persistence: the shopper's last active filter set is stashed in
// localStorage and restored on a bare visit to /search, unless they hit "Clear filters".
// This is the key PREFIX — the actual key is suffixed with the org + storefront (see
// rememberedSearchKey) so remembered filters never bleed across storefronts on one origin.
const SEARCH_FILTERS_KEY = 'sf-remembered-search';
// Params that count as "filters" (keywords deliberately excluded — a bare keyword search
// shouldn't be remembered as a filter, and clearing filters leaves keywords alone).
const SEARCH_FILTER_PARAMS = ['category', 'subcategory', 'segment', 'manufacturer', 'minprice', 'maxprice', 'facet', 'tag', 'favorites'];

const SearchPage: React.FC = () => {
    
    const canSeePricing = CAN(PRICING);
    const canAddToCart = CAN(CART_MANAGEMENT);
    
    const params = new URLSearchParams(window.location.search);
    
    const keywords = params.get('keywords');
    const category = params.get('category');
    const subCategory = params.get('subcategory');
    const segment = params.get('segment');
    const minPrice = params.get('minprice');
    const maxPrice = params.get('maxprice');

    const manufacturer = params.get('manufacturer');

    // Facet filters as repeated ?facet=Key|Value params (e.g. ?facet=Color|Black&facet=Color|White&facet=Form%20Factor|Laptop).
    // Values sharing a key are OR'd; different keys are AND'd (handled by the catalog API).
    const attributeFilters = params.getAll('facet')
        .map((f) => {
            const sep = f.indexOf('|');
            return sep > 0 ? { name: f.slice(0, sep), value: f.slice(sep + 1) } : null;
        })
        .filter((f): f is { name: string; value: string } => f != null && f.value !== '');

    // Product tag filters as repeated ?tag=Value params (OR'd across values; handled by the catalog API).
    const tagFilters = params.getAll('tag').filter((t) => t !== '');
    // "Featured" rides in the URL as a normal ?tag= value (so chips/applied-pills/clear/persistence all
    // treat it like any tag), but it's a per-storefront flag rather than a stored product tag — so split
    // it out and send it as the `featured` search filter instead of a tag-terms match.
    const featuredSelected = tagFilters.includes(FEATURED_TAG);
    // Favorites-only rides as ?favorites=1 so it persists, clears and back-buttons like any other filter.
    const favoritesOnly = params.get('favorites') === '1';
    const realTagFilters = tagFilters.filter((t) => t !== FEATURED_TAG);

    const page = params.get('page');
    const pageSize = params.get('pagesize');
    const minPriceParam = params.get('minprice');
    const maxPriceParam = params.get('maxprice');
    const [searchKeywords, setSearchKeywords] = React.useState<string>(keywords ?? '');
    const [searchSubCategory, setSearchSubCategory] = React.useState<string | undefined>(subCategory ?? undefined);
    const [searchCategory, setSearchCategory] = React.useState<string | undefined>(category ?? undefined);
    const [searchSegment, setSearchSegment] = React.useState<string | undefined>(segment ?? undefined);
    const [searchManufacturer, setSearchManufacturer] = React.useState<string | undefined>(manufacturer ?? undefined);
    const [searchPageNumber, setSearchPageNumber] = React.useState<number>(page ? parseInt(page) : 1);
    const [searchPageSize, setSearchPageSize] = React.useState<number>(pageSize ? parseInt(pageSize) : 25);
    const [searchedMinPrice, setSearchedMinPrice] = React.useState<string>(minPriceParam ?? '');
    const [searchedMaxPrice, setSearchedMaxPrice] = React.useState<string>(maxPriceParam ?? '');
    const [message, setMessage] = React.useState<string | undefined>(undefined);
    const [messageTitle, setMessageTitle] = React.useState<string | undefined>(undefined);
    const searchProducts = useSearchProductsQuery({request: { keywords: keywords ?? '', subCategory: searchSubCategory, category: searchCategory, manufacturer: searchManufacturer, segment: searchSegment, page: searchPageNumber, pageSize: searchPageSize, minPrice: minPrice ? parseFloat(minPrice) : undefined, maxPrice: maxPrice ? parseFloat(maxPrice) : undefined, attributeFilters: attributeFilters.length ? attributeFilters : undefined, tagFilters: realTagFilters.length ? realTagFilters : undefined, featured: featuredSelected || undefined, favoritesOnly: favoritesOnly || undefined }});
    // The active search criteria in the interest-signal shape the BFF expects (see ISearchContext),
    // mirroring the same values fed into the searchProducts request above so favorite/view/cart-add
    // actions taken on a result capture exactly what's on screen. Attribute facets and tag filters both
    // fold into `filters` (tags keyed as 'tag') since the BFF shape only has one filters bucket.
    const searchContext: ISearchContext | undefined = (() => {
        const filters = [
            ...attributeFilters.map((f) => ({ key: f.name, value: f.value })),
            ...realTagFilters.map((t) => ({ key: 'tag', value: t })),
        ];
        const ctx: ISearchContext = {};
        if (keywords) ctx.keywords = keywords;
        if (searchCategory) ctx.category = searchCategory;
        if (searchSegment) ctx.segment = searchSegment;
        if (searchSubCategory) ctx.subCategory = searchSubCategory;
        if (searchManufacturer) ctx.manufacturer = searchManufacturer;
        if (filters.length) ctx.filters = filters;
        return Object.keys(ctx).length ? ctx : undefined;
    })();
    // Per-result searchContext: the base context above plus resultPosition — the 1-based rank of the
    // product within the full (paginated) result set, i.e. the page offset plus the on-page index.
    // Used for the interest-signal calls tied to a specific result card/row (view drill-in, favorite,
    // add-to-cart) rather than the page-level context, which has no single position to report.
    const searchContextForResult = (index: number): ISearchContext => ({
        ...(searchContext ?? {}),
        resultPosition: (searchPageNumber - 1) * searchPageSize + index + 1,
    });
    // The Elastic query now accounts for the storefront's limiting factors, so the reported
    // total is the true, actionable result count — we can show it and the exact page count
    // up front instead of probing for a "More" frontier.
    //
    // ...but that total is still an Elastic *estimate* that can wobble between pages: requesting a
    // later page sometimes returns a smaller total (occasionally with an empty product list). Taken
    // at face value that shrinks totalPages to 1, and the snap-back effect below then bounces the
    // shopper straight back to page 1 — so paging past 1 appears to "revert." Hold the largest total
    // seen for the current search (identity = every search input except the page number) so the page
    // count stays stable while only the page changes; it resets whenever the search itself changes.
    // Feed it from `currentData`, NOT `data`: RTK's `data` keeps the PREVIOUS search's result while a
    // new one is in flight, so on a search change (e.g. selecting the Featured filter) the ref would
    // reset for the new identity and then immediately re-capture the old, larger total — leaving the
    // paginator showing e.g. "1–25 of 38" over 4 products. `currentData` is only the settled result for
    // the current args (undefined while fetching), so it never carries a stale cross-search total.
    const searchIdentity = JSON.stringify({ keywords, searchCategory, searchSubCategory, searchSegment, searchManufacturer, minPrice, maxPrice, attributeFilters, tagFilters, featuredSelected, searchPageSize });
    const stableTotalRef = React.useRef<{ id: string; total: number }>({ id: searchIdentity, total: 0 });
    if (stableTotalRef.current.id !== searchIdentity) stableTotalRef.current = { id: searchIdentity, total: 0 };
    const reportedTotal = searchProducts.currentData?.total ?? 0;
    if (reportedTotal > stableTotalRef.current.total) stableTotalRef.current.total = reportedTotal;
    const totalResults = stableTotalRef.current.total;
    const totalPages = Math.max(1, Math.ceil(totalResults / searchPageSize));
    // The catalog API caps storefront search at 10 pages (requesting more throws), so cap the
    // pages the paginator will navigate to. The label still reports the true result total.
    const MAX_NAV_PAGES = 10;
    const navigablePages = Math.min(totalPages, MAX_NAV_PAGES);
    const categoryDetails = useGetCategoriesQuery();
    // Options for the filter panel's segment/category dropdowns, derived from the storefront category list.
    // (The storefront search response carries no segment metadata, so this is the source of truth.) Categories
    // carry the segment as `segmentId` — a printable label that matches the product's `segment` search value;
    // `segmentLabel` is not populated by the storefront API.
    const segmentOptions = React.useMemo(() => {
        const set = new Set<string>();
        (categoryDetails.data ?? []).forEach((c) => { if (c.segmentId) set.add(c.segmentId); });
        return Array.from(set).sort();
    }, [categoryDetails.data]);
    const categoryOptions = React.useMemo(() => {
        // With a segment selected, list that segment's categories; without one (e.g. arriving from a home
        // page category pod), list all categories so the dropdown is still usable.
        const set = new Set<string>();
        (categoryDetails.data ?? []).forEach((c) => {
            if ((!searchSegment || c.segmentId === searchSegment) && c.label) set.add(c.label);
        });
        return Array.from(set).sort();
    }, [categoryDetails.data, searchSegment]);
    const nav = useNavigate();
    // Named to avoid shadowing the global `location` — this file reads window.location directly.
    const routerLocation = useLocation();
    const L = useLocalization();
    const settings = useSelector((state: RootState) => state.cart);
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const [canActuallyAddToCart, setCanActuallyAddToCart] = React.useState(false);
    const [cartBlocked, setCartBlocked] = React.useState(false);

    // Search page presentation, configured in the admin "Customize Page" > Search tab.
    const storefrontSettings = useGetStorefrontQuery();
    const searchConfig = React.useMemo(
        () => parseSearchPageConfig(storefrontSettings.data?.searchPageConfig),
        [storefrontSettings.data?.searchPageConfig]
    );
    // Memoized because SuggestingSearchBox keys its own memos (resolved styles, requested columns) on
    // this object's identity — re-parsing the blob inline would hand it a new object every render and
    // rebuild all of them. This page re-renders on every keystroke, so it matters most here.
    const searchAutocomplete = React.useMemo(
        () => parseAutocompleteConfig(storefrontSettings.data?.searchPageConfig),
        [storefrontSettings.data?.searchPageConfig]
    );
    const viewMode = searchConfig.viewMode ?? 'rows';
    const facetsOn = searchConfig.facetSidebar?.enabled === true;
    const facetLayout = searchConfig.facetSidebar?.layout ?? 'panel';
    const priceFilterOn = searchConfig.facetSidebar?.priceFilter === true;
    // Luminance of the storefront background, so the filter panel's own text/chips flip light-on-dark.
    const panelIsDark = React.useMemo(() => isDarkColor(storefrontSettings.data?.backgroundColor), [storefrontSettings.data?.backgroundColor]);
    const panelOn = facetsOn && facetLayout === 'panel';
    // Sticky search chrome (filter panel + search/paginator row stay pinned; only results scroll).
    // The header's own sticky flag lives in headerConfig; we read it so the sticky search controls
    // can offset below a pinned header instead of sliding under it.
    const stickyControls = searchConfig.stickyControls === true;
    const headerSticky = React.useMemo(
        () => parseHeaderConfig(storefrontSettings.data?.headerConfig).sticky === true,
        [storefrontSettings.data?.headerConfig]
    );
    // A sticky header search ROW is a persistent search box visible on every page (it pins on scroll),
    // so the search page's own search input is redundant — hide it (AC-970). Only when both searchRow
    // and searchRowSticky are on; a non-sticky search row scrolls away, so the page bar is still useful.
    const stickySearchRow = React.useMemo(() => {
        const hc = parseHeaderConfig(storefrontSettings.data?.headerConfig);
        return (storefrontSettings.data?.useHeaderSearch ?? false) && hc.searchRow === true && hc.searchRowSticky === true;
    }, [storefrontSettings.data?.headerConfig, storefrontSettings.data?.useHeaderSearch]);
    // A pinned filter rail is capped at the viewport and scrolls internally, which is how a short
    // result set with many facets ends up with a second scrollbar. Measure the rail: when it cannot
    // fit, we stop pinning it (see .sf-search-aside-tall) so the page scroll reveals it instead.
    const asideOffset = React.useCallback(() => {
        if (!stickyControls) return 0;
        const navbar = headerSticky
            ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sf-navbar-height')) || 80
            : 0;
        // The 24px covers the rail's own top offset (0.5em) and bottom breathing room (1.5em).
        return navbar + 24;
    }, [stickyControls, headerSticky]);
    const asideFit = useFitsViewport(asideOffset);
    // The page background, applied behind the sticky controls so scrolling results don't show through.
    const pageBg = storefrontSettings.data?.backgroundColor;
    // Filter panel is a left rail on wide screens and a floating modal on narrow ones. Default: shown on
    // wide, hidden on narrow. The toggle button (left of the search bar) flips it; crossing the breakpoint
    // resets to that default.
    const NARROW_QUERY = '(max-width: 900px)';
    const [isNarrow, setIsNarrow] = React.useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches);
    const [filtersOpen, setFiltersOpen] = React.useState(() => !(typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches));
    React.useEffect(() => {
        const mq = window.matchMedia(NARROW_QUERY);
        const handler = (e: MediaQueryListEvent) => { setIsNarrow(e.matches); setFiltersOpen(!e.matches); };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    // Remember filters across visits. Whenever the URL carries active filters, stash the query
    // string; on a bare visit to /search (no params at all) restore the last remembered set.
    // Scoped to the current org + storefront so remembered filters never bleed across storefronts
    // sharing this origin's localStorage.
    const rememberedSearchKey = React.useMemo(() => {
        const org = localStorage.getItem('organization') ?? '';
        const store = localStorage.getItem('storefront') ?? '';
        return `${SEARCH_FILTERS_KEY}:${org}:${store}`;
    }, []);
    const currentSearch = window.location.search;
    // Set by navigations that explicitly mean "start over" (Home's Browse All / nav pods) — see the
    // restore effect below. Read once from the arrival location; later in-page navigations don't
    // carry it, which is fine since the restore only runs on mount.
    const arrivedFresh = (routerLocation.state as { fresh?: boolean } | null)?.fresh === true;
    React.useEffect(() => {
        const p = new URLSearchParams(currentSearch);
        const hasFilters = SEARCH_FILTER_PARAMS.some((k) => p.getAll(k).some((v) => v.trim() !== ''));
        if (hasFilters) localStorage.setItem(rememberedSearchKey, currentSearch);
    }, [currentSearch, rememberedSearchKey]);
    React.useEffect(() => {
        // An explicit "browse" navigation (Home's Browse All button / nav pods) asks for a clean
        // slate — honour it rather than silently reinstating the last filter set. The remembered
        // set is left in storage: this suppresses the restore for this arrival only, it doesn't
        // forget the filters (that's "Clear filters").
        if (arrivedFresh) return;
        const p = new URLSearchParams(window.location.search);
        const hasAnyParam = Array.from(p.keys()).some((k) => (p.get(k) ?? '').trim() !== '');
        if (hasAnyParam) return; // an explicit search/filter — respect it, don't override
        const saved = localStorage.getItem(rememberedSearchKey);
        if (saved && saved !== window.location.search) nav(`/search${saved}`, { replace: true });
        // Runs once on arrival at a bare /search.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The results grid itself. CardSkeletonResults measures it to fill exactly one row — the
    // skeleton is a fragment rendered into this container, so it has no element of its own.
    const cardGridRef = React.useRef<HTMLDivElement>(null);
    const cardStyle = React.useMemo(() => cardStyleFrom(searchConfig.card), [searchConfig.card]);
    const nameStyle = React.useMemo(() => textStyleFrom(searchConfig.title, 'title'), [searchConfig.title]);
    const priceStyle = React.useMemo(() => priceStyleFrom(searchConfig.price), [searchConfig.price]);
    const imageStyle = React.useMemo(() => elementStyleFrom(searchConfig.image, 'image'), [searchConfig.image]);
    const containerStyle = React.useMemo(() => containerStyleFrom(searchConfig.container), [searchConfig.container]);
    const paginatorContainerStyle = React.useMemo(() => elementStyleFrom(searchConfig.paginatorContainer), [searchConfig.paginatorContainer]);
    const paginatorCellStyle = React.useMemo(() => elementStyleFrom(searchConfig.paginatorCell), [searchConfig.paginatorCell]);
    const breadcrumbContainerStyle = React.useMemo(() => elementStyleFrom(searchConfig.breadcrumbContainer), [searchConfig.breadcrumbContainer]);
    const breadcrumbItemStyle = React.useMemo(() => elementStyleFrom(searchConfig.breadcrumbItem), [searchConfig.breadcrumbItem]);
    // Per-row meta chips: the whole category breadcrumb and the whole manufacturer label
    // each styled as a single chip. Applied inline (these spans aren't theme-forced).
    const categoryChipStyle = React.useMemo(() => elementStyleFrom(searchConfig.categoryChip), [searchConfig.categoryChip]);
    const manufacturerChipStyle = React.useMemo(() => elementStyleFrom(searchConfig.manufacturerChip), [searchConfig.manufacturerChip]);
    const searchBarStyle = React.useMemo(() => elementStyleFrom(searchConfig.searchBar, 'input'), [searchConfig.searchBar]);
    // On a skinny (narrow) layout the header already provides a search box when useHeaderSearch is on, so the
    // page's own search input is redundant — hide it to reclaim the row (and portal the filter toggle/paginator
    // into the header). On wider layouts both can coexist — UNLESS a sticky header search row is present, in
    // which case we drop just the page's search input but keep the toggle/paginator inline on the page.
    const hidePageSearchBar = isNarrow && (storefrontSettings.data?.useHeaderSearch ?? false);
    const hideSearchInputOnly = stickySearchRow && !hidePageSearchBar;
    // When the page search bar is hidden we relocate the filter toggle + paginator into the header via a portal
    // (the NavBar renders a stable `#sf-search-header-controls` slot). Both controls go into that one slot so
    // they group together and flex down to a new row as a unit. Re-resolve when the condition flips so we pick
    // up the slot once header search has loaded.
    const [headerControlsSlot, setHeaderControlsSlot] = React.useState<HTMLElement | null>(null);
    React.useEffect(() => {
        setHeaderControlsSlot(document.getElementById('sf-search-header-controls'));
    }, [hidePageSearchBar]);
    const facetPanelStyle = React.useMemo(() => elementStyleFrom(searchConfig.facetPanel), [searchConfig.facetPanel]);
    const facetHeadingStyle = React.useMemo(() => textStyleFrom(searchConfig.facetHeading, 'heading'), [searchConfig.facetHeading]);
    const addToCartStyle = React.useMemo(() => elementStyleFrom(searchConfig.addToCartButton), [searchConfig.addToCartButton]);
    const addToCartLabelStyle = React.useMemo(() => elementStyleFrom(searchConfig.addToCartLabel, 'label'), [searchConfig.addToCartLabel]);
    // Hover styling for the product tile (cards + rows). `:hover` can't be expressed as an
    // inline style prop, so emit a scoped stylesheet rule (with !important, to beat the
    // theme's forced .card / .hoverable-card rules) plus a short transition to animate it.
    // The hover dict also picks up the `hover` part of the named style the RESTING card references
    // (resolveHoverPart's second argument), merged under the local cardHover. No ref and no local
    // hover dict => undefined => no rule emitted, exactly as before.
    const cardHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(resolveHoverPart(searchConfig.cardHover, searchConfig.card), 'hover');
        return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [searchConfig.cardHover, searchConfig.card]);
    // Same hover as a style object for ProductCard to apply inline — a card whose resting style is
    // applied inline-!important cannot be repainted by the stylesheet rule above. See ProductCard.
    const cardHoverStyle = React.useMemo(() => {
        const s = elementStyleFrom(resolveHoverPart(searchConfig.cardHover, searchConfig.card), 'hover');
        return Object.keys(s).length ? s : undefined;
    }, [searchConfig.cardHover, searchConfig.card]);
    // Button hover: the `hover` part of the button style the add-to-cart button references (there is
    // no local add-to-cart hover config today). Covers the rows-view button (.add-to-cart-button)
    // and the floating card button (.sf-card-add-to-cart).
    const addToCartHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(hoverPartFor(searchConfig.addToCartButton), 'hover');
        return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [searchConfig.addToCartButton]);
    // Chips are forced by the theme with `!important`, so their overrides go through a ref.
    const chipRef = searchConfig.breadcrumbItem ? importantStyleRef(breadcrumbItemStyle) : undefined;
    // Row cards: the dark theme forces `.hoverable-card { background / border-color
    // !important }`, so the configured card style must be applied as inline `!important`
    // too (same as card mode). Plain `style=` would silently lose on a dark storefront.
    const rowCardRef = Object.keys(cardStyle).length ? importantStyleRef(cardStyle) : undefined;

    useEffect(() => {
        if(settings.selectedCartId && getPreparedCart.data) {
            const canAddToPreparedCart = getPreparedCart.data.canAddAdditionalItems ?? false;
            setCanActuallyAddToCart(canAddToPreparedCart && canAddToCart);
            setCartBlocked(!canAddToPreparedCart);
        }
        else{
            setCanActuallyAddToCart(canAddToCart);
            setCartBlocked(false);
        }
    }, [canAddToCart, getPreparedCart.data, settings.selectedCartId]);
    
    React.useEffect(() => {
        setSearchKeywords(keywords ?? '');
    }, [keywords]);
    React.useEffect(() => {
        setSearchCategory(category ?? undefined);
    }, [category]);
    React.useEffect(() => {
        setSearchSubCategory(subCategory ?? undefined);
    }, [subCategory]);
    React.useEffect(() => {
        setSearchSegment(segment ?? undefined);
    }, [segment]);
    React.useEffect(() => {
        setSearchManufacturer(manufacturer ?? undefined);
    }, [manufacturer]);
    React.useEffect(() => {
        setSearchedMinPrice(minPriceParam ?? '');
    }, [minPriceParam]);
    React.useEffect(() => {
        setSearchedMaxPrice(maxPriceParam ?? '');
    }, [maxPriceParam]);

    React.useEffect(() => {
        if (searchProducts.error) {
            setMessage('There was an error. Please try again');
            setMessageTitle('Error');
            return;
        }
        // Use currentData (not data): while a new page is fetching, `data` still holds
        // the previous page.
        const data = searchProducts.currentData;
        if (!data) return;
        if (data.products.length === 0) {
            setMessageTitle(undefined);
            setMessage(L('localization_search_no_products'));
        } else {
            setMessage(undefined);
            setMessageTitle(undefined);
        }
    }, [searchProducts.currentData, searchProducts.error]);

    // If the current page ends up past the last navigable page (e.g. after changing the page
    // size or applying a filter that shrank the result set), snap back to it.
    React.useEffect(() => {
        if (searchProducts.data && searchPageNumber > navigablePages) {
            setSearchPageNumber(navigablePages);
        }
    }, [navigablePages, searchPageNumber, searchProducts.data]);
    
    const getSymbol = (isFirstParam: boolean) => isFirstParam ? '?' : '&';

    // Accepts the submitted text rather than always reading state: when SearchBox submits a suggestion
    // the shopper picked, the corresponding setSearchKeywords has not been applied yet, so reading state
    // here would search for what they had typed instead of what they chose.
    const search = (submitted?: string) => {
        const effectiveKeywords = submitted ?? searchKeywords;
        let isFirstParam = true;
        let searchPath = `/search`
        if(effectiveKeywords){
            searchPath += `${getSymbol(isFirstParam)}keywords=${encodeURIComponent(effectiveKeywords)}`;
            isFirstParam = false;
        }
        if (searchCategory) {
            searchPath += `${getSymbol(isFirstParam)}category=${encodeURIComponent(searchCategory)}`;
            isFirstParam = false;
        }
        if (searchSubCategory) {
            searchPath += `${getSymbol(isFirstParam)}subcategory=${encodeURIComponent(searchSubCategory)}`;
            isFirstParam = false;
        }
        if (searchSegment) {
            searchPath += `${getSymbol(isFirstParam)}segment=${encodeURIComponent(searchSegment)}`;
            isFirstParam = false;
        }
        if (searchManufacturer) {
            searchPath += `${getSymbol(isFirstParam)}manufacturer=${encodeURIComponent(searchManufacturer)}`;
            isFirstParam = false;
        }
        if (searchedMinPrice) {
            searchPath += `${getSymbol(isFirstParam)}minprice=${encodeURIComponent(searchedMinPrice)}`;
            isFirstParam = false;
        }
        if (searchedMaxPrice) {
            searchPath += `${getSymbol(isFirstParam)}maxprice=${encodeURIComponent(searchedMaxPrice)}`;
            isFirstParam = false;
        }
        // Tag and favorites filters aren't tied to the keywords, so re-running the search keeps them
        // (same rule as navSearch). Attribute facets are still dropped — a new keyword set can land
        // outside the category those values were aggregated within.
        for (const t of tagFilters) {
            searchPath += `${getSymbol(isFirstParam)}tag=${encodeURIComponent(t)}`;
            isFirstParam = false;
        }
        if (favoritesOnly) {
            searchPath += `${getSymbol(isFirstParam)}favorites=1`;
            isFirstParam = false;
        }
        nav(searchPath);
    }

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const navSearch = (keyword?: string, subCategory?: string, category?: string, segment?: string, manufacturer?: string, minP?: string, maxP?: string) => {
        setSearchPageNumber(1);
        let searchPath = `/search?keywords=${encodeURIComponent(keyword ?? '')}`;
        if (subCategory) {
            searchPath += `&subcategory=${encodeURIComponent(subCategory)}`;
        }
        if (category) {
            searchPath += `&category=${encodeURIComponent(category)}`;
        }
        if (segment) {
            searchPath += `&segment=${encodeURIComponent(segment)}`;
        }
        if (manufacturer) {
            searchPath += `&manufacturer=${encodeURIComponent(manufacturer)}`;
        }
        if (minP !== undefined && minP !== '') {
            searchPath += `&minprice=${encodeURIComponent(minP)}`;
        }
        if (maxP !== undefined && maxP !== '') {
            searchPath += `&maxprice=${encodeURIComponent(maxP)}`;
        }
        // Tag and favorites filters are NOT scoped to a segment/category, so they survive a
        // navigation change and are carried through here. (Attribute facets are category-scoped —
        // they'd be meaningless under a different category — so those are still dropped.) Without
        // this, picking "All categories" wiped a tag the shopper had applied from a result card.
        for (const t of tagFilters) searchPath += `&tag=${encodeURIComponent(t)}`;
        if (favoritesOnly) searchPath += '&favorites=1';
        nav(searchPath);
        scrollToTop();
    }

    // Clear-filters: also forget the remembered set so it isn't restored on the next visit.
    // Navigates explicitly rather than via navSearch — navSearch deliberately carries tag/favorites
    // filters through, and "Clear filters" is the one action that must drop them too.
    const clearFilters = () => {
        localStorage.removeItem(rememberedSearchKey);
        setSearchPageNumber(1);
        nav(`/search?keywords=${encodeURIComponent(searchKeywords ?? '')}`);
        scrollToTop();
    };

    // Rebuild the URL preserving the current dimensions but replacing the facet filters. Facets
    // are cleared automatically by navSearch on any segment/category change (it drops them), so
    // this is only used by the sidebar to add/remove attribute values within the current results.
    const updateFacetParams = (nextFacets: { name: string; value: string }[], categoryOverride?: string) => {
        setSearchPageNumber(1);
        const category = categoryOverride ?? searchCategory;
        let path = `/search?keywords=${encodeURIComponent(searchKeywords ?? '')}`;
        if (searchSubCategory) path += `&subcategory=${encodeURIComponent(searchSubCategory)}`;
        if (category) path += `&category=${encodeURIComponent(category)}`;
        if (searchSegment) path += `&segment=${encodeURIComponent(searchSegment)}`;
        if (searchManufacturer) path += `&manufacturer=${encodeURIComponent(searchManufacturer)}`;
        if (searchedMinPrice) path += `&minprice=${encodeURIComponent(searchedMinPrice)}`;
        if (searchedMaxPrice) path += `&maxprice=${encodeURIComponent(searchedMaxPrice)}`;
        for (const f of nextFacets) path += `&facet=${encodeURIComponent(`${f.name}|${f.value}`)}`;
        for (const t of tagFilters) path += `&tag=${encodeURIComponent(t)}`;
        if (favoritesOnly) path += '&favorites=1';
        nav(path);
        scrollToTop();
    };

    // Attribute-chip click: toggle the facet, and — when nothing scopes the aggregations yet — adopt the
    // clicked product's category as the active category so the filter has a category to resolve within.
    const filterByAttribute = (name: string, value: string, productCategory?: string) => {
        const exists = attributeFilters.some((f) => f.name === name && f.value === value);
        const next = exists
            ? attributeFilters.filter((f) => !(f.name === name && f.value === value))
            : [...attributeFilters, { name, value }];
        updateFacetParams(next, searchCategory || productCategory);
    };

    const toggleFacet = (name: string, value: string) => {
        const exists = attributeFilters.some((f) => f.name === name && f.value === value);
        const next = exists
            ? attributeFilters.filter((f) => !(f.name === name && f.value === value))
            : [...attributeFilters, { name, value }];
        updateFacetParams(next);
    };

    // Rebuild the URL preserving the current dimensions + attribute facets, replacing the tag filters.
    const updateTagParams = (nextTags: string[]) => {
        setSearchPageNumber(1);
        let path = `/search?keywords=${encodeURIComponent(searchKeywords ?? '')}`;
        if (searchSubCategory) path += `&subcategory=${encodeURIComponent(searchSubCategory)}`;
        if (searchCategory) path += `&category=${encodeURIComponent(searchCategory)}`;
        if (searchSegment) path += `&segment=${encodeURIComponent(searchSegment)}`;
        if (searchManufacturer) path += `&manufacturer=${encodeURIComponent(searchManufacturer)}`;
        if (searchedMinPrice) path += `&minprice=${encodeURIComponent(searchedMinPrice)}`;
        if (searchedMaxPrice) path += `&maxprice=${encodeURIComponent(searchedMaxPrice)}`;
        for (const f of attributeFilters) path += `&facet=${encodeURIComponent(`${f.name}|${f.value}`)}`;
        for (const t of nextTags) path += `&tag=${encodeURIComponent(t)}`;
        if (favoritesOnly) path += '&favorites=1';
        nav(path);
        scrollToTop();
    };

    // Toggle a tag filter — from the sidebar checkboxes, the applied-tag pills, or a tag decoration on a
    // result card. Unlike filterByAttribute, a tag click does NOT adopt the clicked product's category:
    // attribute facets can only be resolved within a category scope, but tags are global — the tag facet
    // list is optional and the applied-tag pills render with no category loaded (see
    // FacetFilters.showAppliedTags). Adopting it silently narrowed an unfiltered search to that one
    // product's category.
    const toggleTag = (value: string) => {
        const next = tagFilters.includes(value) ? tagFilters.filter((t) => t !== value) : [...tagFilters, value];
        updateTagParams(next);
    };

    // Toggle favorites-only, preserving the rest of the query. Resets to page 1 — the favorites subset
    // is unrelated to where the shopper was paged to.
    const toggleFavoritesOnly = (on: boolean) => {
        setSearchPageNumber(1);
        const next = new URLSearchParams(window.location.search);
        if (on) next.set('favorites', '1');
        else next.delete('favorites');
        next.delete('page');
        nav(`/search?${next.toString()}`);
        scrollToTop();
    };

    // Apply a price range while preserving the current dimensions and facet filters. Resets to page 1.
    const updatePriceParams = (min?: string, max?: string) => {
        setSearchPageNumber(1);
        let path = `/search?keywords=${encodeURIComponent(searchKeywords ?? '')}`;
        if (searchSubCategory) path += `&subcategory=${encodeURIComponent(searchSubCategory)}`;
        if (searchCategory) path += `&category=${encodeURIComponent(searchCategory)}`;
        if (searchSegment) path += `&segment=${encodeURIComponent(searchSegment)}`;
        if (searchManufacturer) path += `&manufacturer=${encodeURIComponent(searchManufacturer)}`;
        if (min) path += `&minprice=${encodeURIComponent(min)}`;
        if (max) path += `&maxprice=${encodeURIComponent(max)}`;
        for (const f of attributeFilters) path += `&facet=${encodeURIComponent(`${f.name}|${f.value}`)}`;
        for (const t of tagFilters) path += `&tag=${encodeURIComponent(t)}`;
        if (favoritesOnly) path += '&favorites=1';
        nav(path);
        scrollToTop();
    };

    // Editing the keywords invalidates the current page: results change underneath the paginator, so
    // staying on page 4 of the previous query would show an empty page.
    const handleKeywordChange = (value: string) => {
        setSearchKeywords(value);
        setSearchPageNumber(1);
    }
    
    const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setSearchedMinPrice(data.value.replace(/[^\d.]/g, ''));
        setSearchPageNumber(1);
    }
    const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setSearchedMaxPrice(data.value.replace(/[^\d.]/g, ''));
        setSearchPageNumber(1);
    }
    
    const clearMessage = () => {
        setMessage(undefined);
        setMessageTitle(undefined);
    }

    const SkeletonResults = () => (
        <div className="search-results-list" style={{width: '100%', marginTop: '1em', display: 'flex', flexDirection: 'column', gap: '10px'}}>
            {Array.from({length: 6}).map((_, i) => (
                <div key={i} className="search-result" style={{display: 'flex', alignItems: 'center', gap: 0, minHeight: 116}}>
                    {/* image placeholder */}
                    <div className="skeleton-box" style={{width: 100, height: 100, flex: '0 0 100px', margin: 8}}/>
                    {/* text lines */}
                    <div style={{flex: '1 1 0', paddingLeft: '1em', display: 'flex', flexDirection: 'column', gap: 10}}>
                        <div className="skeleton-box" style={{height: 22, width: '60%'}}/>
                        <div className="skeleton-box" style={{height: 14, width: '35%'}}/>
                        <div className="skeleton-box" style={{height: 14, width: '45%'}}/>
                    </div>
                    {/* price placeholder */}
                    <div style={{width: 200, flex: '0 0 200px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '1em', gap: 10}}>
                        <div className="skeleton-box" style={{height: 26, width: 80}}/>
                    </div>
                </div>
            ))}
        </div>
    );
    
    // Exactly one row of placeholders, like the home page's section skeleton. A fixed count (it was
    // 8) only lines up at the one width where it divides evenly; anywhere else it leaves a ragged
    // part-row that reads as a broken grid rather than as loading.
    //
    // Measured off the GRID, not off this component: the skeleton is a fragment rendered straight
    // into `.card-container`, and that container's gap is merchant-configurable
    // (searchPageConfig.cardGap), so the spacing has to be read from the live element.
    //
    // The cards no longer hardcode `width: 250` either — `.card` sizes itself per breakpoint (2-up
    // on narrow), and a fixed width would have the placeholder wrapping differently from the real
    // results it stands in for.
    const CardSkeletonResults = () => {
        const [cardCount, setCardCount] = React.useState(5);
        React.useLayoutEffect(() => {
            const el = cardGridRef.current;
            if (!el) return;
            const measure = () => {
                const card = el.firstElementChild as HTMLElement | null;
                const cardWidth = card?.getBoundingClientRect().width ?? 0;
                if (cardWidth <= 0) return;
                const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
                setCardCount(Math.max(1, Math.floor((el.clientWidth + gap) / (cardWidth + gap))));
            };
            measure();
            const observer = new ResizeObserver(measure);
            observer.observe(el);
            return () => observer.disconnect();
        }, []);
        return (
            <>
                {Array.from({length: cardCount}).map((_, i) => (
                    <div key={i} className="card" style={{minHeight: 300, padding: 12, boxSizing: 'border-box'}}>
                        <div className="skeleton-box" style={{height: 180, width: '100%'}}/>
                        <div className="skeleton-box" style={{height: 18, width: '80%', marginTop: 12}}/>
                        <div className="skeleton-box" style={{height: 22, width: '40%', marginTop: 10}}/>
                    </div>
                ))}
            </>
        );
    };

    const getNormalizedCategory = (catIdentifier: string) => {
        if(categoryDetails?.data){
            const cat = categoryDetails.data.find(c => c.identifier?.toLowerCase() === catIdentifier.toLowerCase());
            if(cat){
                return cat.label;
            }
        }
        return catIdentifier;
    }
    
    const getCategoryElement = (product: IProduct) => {
        const parts: { label: string, onClick: () => void }[] = [];
        if (product.segment) {
            parts.push({
                label: product.segment,
                onClick: () => {
                    setSearchPageNumber(1);
                    navSearch(searchKeywords, undefined, undefined, product.segment, searchManufacturer, undefined, undefined)
                }
            });
        }
        if (product.category) {
            parts.push({
                label: product.category,
                onClick: () => {
                    setSearchPageNumber(1);
                    navSearch(searchKeywords, undefined, product.category, product.segment, searchManufacturer, undefined, undefined)
                }
            });
        }
        if (product.subCategory) {
            parts.push({
                label: product.subCategory,
                onClick: () => {
                    setSearchPageNumber(1);
                    navSearch(searchKeywords, product.subCategory, product.category, product.segment, searchManufacturer, undefined, undefined)
                }
            });
        }
        if (parts.length === 0) return null;
        return (
            <span style={{fontSize: 14, ...(Object.keys(categoryChipStyle).length ? { display: 'inline-block', ...categoryChipStyle } : {})}}>
                Category:
                {parts.map((part, idx) => (
                    <span key={part.label}>
                        {idx > 0 && <span style={{margin: '0 4px'}}>&gt;</span>}
                        <span
                            style={{
                                color: '#0078d4',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                marginLeft: idx === 0 ? 4 : 0
                            }}
                            onClick={e => {
                                e.stopPropagation();
                                setTimeout(part.onClick, 0);
                            }}
                        >
                            {part.label}
                        </span>
                    </span>
                ))}
            </span>
        );
    }
    
    // The panel-layout filter control, rendered both in the sticky sidebar (wide) and the drawer (narrow).
    const renderPanelFilters = () => (
        <FacetFilters
            layout='panel'
            segment={searchSegment}
            category={searchCategory}
            subCategory={searchSubCategory}
            keywords={searchKeywords}
            active={attributeFilters}
            onToggle={toggleFacet}
            segmentOptions={segmentOptions}
            categoryOptions={categoryOptions}
            onSelectSegment={(seg) => navSearch(searchKeywords, undefined, undefined, seg, searchManufacturer)}
            onSelectCategory={(cat) => navSearch(searchKeywords, undefined, cat, searchSegment, searchManufacturer)}
            onSelectSubCategory={(sub) => navSearch(searchKeywords, sub, searchCategory, searchSegment, searchManufacturer)}
            manufacturer={searchManufacturer}
            onSelectManufacturer={(mfr) => navSearch(searchKeywords, searchSubCategory, searchCategory, searchSegment, mfr)}
            onClearAll={clearFilters}
            isDark={panelIsDark}
            priceFilter={priceFilterOn}
            minPrice={minPrice ?? undefined}
            maxPrice={maxPrice ?? undefined}
            onApplyPrice={updatePriceParams}
            panelStyle={facetPanelStyle}
            headingStyle={facetHeadingStyle}
            tagsEnabled={storefrontSettings.data?.enableProductTags ?? false}
            includeFeaturedTag={true}
            activeTags={tagFilters}
            onToggleTag={toggleTag}
            favoritesOnly={favoritesOnly}
            onToggleFavoritesOnly={toggleFavoritesOnly}
        />
    );

    // The filter toggle and the paginator render inline in the on-page controls row on wide layouts, and get
    // portaled into the header (see hidePageSearchBar) when the page's own search bar is hidden on a skinny layout.
    const filterToggleButton = panelOn ? (
        <Button
            size="large"
            appearance={panelIsDark ? 'transparent' : 'secondary'}
            className={panelIsDark ? 'sf-filter-toggle-dark' : undefined}
            icon={filtersOpen ? <PanelLeftContractRegular /> : <PanelLeftExpandRegular />}
            aria-label={filtersOpen ? 'Hide filters' : 'Show filters'}
            title={filtersOpen ? 'Hide filters' : 'Show filters'}
            onClick={() => setFiltersOpen(o => !o)}
        />
    ) : null;
    const paginatorEl = (searchProducts.data && totalResults > 0 && totalPages > 1) ? (
        <Paginator
            page={searchPageNumber}
            totalPages={navigablePages}
            total={totalResults}
            pageSize={searchPageSize}
            isDark={panelIsDark}
            containerStyle={searchConfig.paginatorContainer ? paginatorContainerStyle : undefined}
            cellStyle={searchConfig.paginatorCell ? paginatorCellStyle : undefined}
            onChange={(target) => { clearMessage(); setSearchPageNumber(target); scrollToTop(); }}
            onPageSizeChange={(size) => { setSearchPageSize(size); setSearchPageNumber(1); }}
        />
    ) : null;

    return (
        // Shell spans content-inner and centers the [filter rail | 1200px content] group, so the filter
        // panel sits to the LEFT of the 1200px content block (in the content-inner gutter), not inside it.
        <div
            className={`sf-search-shell${stickyControls ? ' sf-sticky-controls' : ''}${stickyControls && headerSticky ? ' sf-sticky-header' : ''}`}
            style={pageBg ? ({ '--sf-page-bg': pageBg } as React.CSSProperties) : undefined}
        >
            {panelOn && !isNarrow && filtersOpen && (
                <aside
                    ref={asideFit.ref as React.RefObject<HTMLElement>}
                    // Too tall to pin without growing its own scrollbar — let it scroll with the page.
                    className={`sf-search-aside${asideFit.overflows ? ' sf-search-aside-tall' : ''}`}
                >
                    {renderPanelFilters()}
                </aside>
            )}
            <div className="sf-search-main">
            {!hidePageSearchBar && (!hideSearchInputOnly || filterToggleButton || paginatorEl) && (
            <div className="sf-search-controls" style={{margin: '1em'}}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {filterToggleButton}
                    {/* A sticky header search row already provides a persistent search box, so the page's own
                        input is dropped here (the toggle + paginator stay inline). */}
                    {!hideSearchInputOnly && (
                    <SearchBox
                        style={{ flex: 2, minWidth: 180, ...searchBarStyle }}
                        placeholder={L('localization_search_placeholder')}
                        value={searchKeywords}
                        onValueChange={handleKeywordChange}
                        onSearch={search}
                        suggestionsAvailable={storefrontSettings.data?.enableSearchAutocomplete}
                        autocomplete={searchAutocomplete}
                    />
                    )}
                    {/* Compact paginator sits to the right of the search bar so it doesn't take its own row. */}
                    {paginatorEl}
                </div>
                {/*For future price filtering. NB: this block predates the SearchBox extraction — reviving it
                   needs the Input import back, and its onKeyDown should just be dropped (SearchBox owns Enter now).*/}
{/*                <div style={{paddingTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Input
                        style={{ flex: 1, minWidth: 100 }}
                        placeholder='Min Price'
                        type='number'
                        value={searchedMinPrice}
                        onChange={handleMinPriceChange}
                        onKeyDown={handleKeywordKeyDown}
                        contentAfter={<span style={{fontSize:'90%', color:'#888', marginLeft:4}}>Min</span>}
                    />
                    <Input
                        style={{ flex: 1, minWidth: 100 }}
                        placeholder='Max Price'
                        type='number'
                        value={searchedMaxPrice}
                        onChange={handleMaxPriceChange}
                        onKeyDown={handleKeywordKeyDown}
                        contentAfter={<span style={{fontSize:'90%', color:'#888', marginLeft:4}}>Max</span>}
                    />
                </div>*/}
            </div>
            )}
            {/* Skinny layout with header search on: the on-page controls row is gone, so relocate the filter
                toggle and paginator together into their single header slot (see NavBar) so they group and
                wrap as one unit. */}
            {hidePageSearchBar && headerControlsSlot && (filterToggleButton || paginatorEl) && createPortal(
                <>{filterToggleButton}{paginatorEl}</>,
                headerControlsSlot
            )}
            <div style={{ margin: '0.5em 0' }}>
                {/* When the filter panel is on, segment/category/sub-category/manufacturer are shown and
                    controlled there instead of as breadcrumb chips. */}
                {!facetsOn && (searchSegment || searchCategory || searchSubCategory || searchManufacturer) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'wrap', ...breadcrumbContainerStyle }}>
                        {/* Hierarchical chips: Segment > Category > Subcategory */}
                        {((): React.ReactNode => {
                            const chips = [];
                            if (searchSegment) {
                                chips.push({
                                    type: 'Segment',
                                    label: `${searchSegment}`,
                                    onClick: () => navSearch(searchKeywords, undefined, undefined, undefined, searchManufacturer)
                                });
                            }
                            if (searchCategory) {
                                chips.push({
                                    type: 'Category',
                                    label: `${searchCategory}`,
                                    onClick: () => navSearch(searchKeywords, undefined, undefined, searchSegment, searchManufacturer)
                                });
                            }
                            if (searchSubCategory) {
                                chips.push({
                                    type: 'Sub-Category',
                                    label: `${searchSubCategory}`,
                                    onClick: () => navSearch(searchKeywords, undefined, searchCategory, searchSegment, searchManufacturer)
                                });
                            }
                            // Render chips and store count for manufacturer margin
                            const chipNodes = chips.map((chip, idx) => (
                                <React.Fragment key={chip.label}>
                                    {idx > 0 && <span style={{color: '#888'}}>&gt;</span>}
                                    <div
                                        ref={chipRef}
                                        className="search-filter-chip"
                                        onClick={chip.onClick}
                                    >
                                        <b>{chip.type}:</b> {chip.label} &times;
                                    </div>
                                </React.Fragment>
                            ));
                            // Render chips and manufacturer chip with correct margin
                            return <div style={{marginLeft: '1em'}}>
                                {chipNodes}
                                {searchManufacturer && (
                                    <div
                                        ref={chipRef}
                                        className="search-filter-chip"
                                        style={{ marginLeft: chipNodes.length > 0 ? 12 : 0 }}
                                        onClick={() => { navSearch(searchKeywords, searchSubCategory, searchCategory, searchSegment, undefined) }}
                                    >
                                        <b>Manufacturer:</b> {searchManufacturer} &times;
                                    </div>
                                )}
                            </div>;
                        })()}
                    </div>
                )}
            </div>
            {facetsOn && facetLayout === 'inline' && (
                <FacetFilters
                    layout='inline'
                    segment={searchSegment}
                    category={searchCategory}
                    subCategory={searchSubCategory}
                    keywords={searchKeywords}
                    active={attributeFilters}
                    onToggle={toggleFacet}
                    segmentOptions={segmentOptions}
                    categoryOptions={categoryOptions}
                    onSelectSegment={(seg) => navSearch(searchKeywords, undefined, undefined, seg, searchManufacturer)}
                    onSelectCategory={(cat) => navSearch(searchKeywords, undefined, cat, searchSegment, searchManufacturer)}
                    onSelectSubCategory={(sub) => navSearch(searchKeywords, sub, searchCategory, searchSegment, searchManufacturer)}
                    manufacturer={searchManufacturer}
                    onSelectManufacturer={(mfr) => navSearch(searchKeywords, searchSubCategory, searchCategory, searchSegment, mfr)}
                    onClearAll={clearFilters}
                    isDark={panelIsDark}
                    priceFilter={priceFilterOn}
                    minPrice={minPrice ?? undefined}
                    maxPrice={maxPrice ?? undefined}
                    onApplyPrice={updatePriceParams}
                    panelStyle={facetPanelStyle}
            headingStyle={facetHeadingStyle}
                    tagsEnabled={storefrontSettings.data?.enableProductTags ?? false}
                    includeFeaturedTag={true}
                    activeTags={tagFilters}
                    onToggleTag={toggleTag}
                    favoritesOnly={favoritesOnly}
                    onToggleFavoritesOnly={toggleFavoritesOnly}
                />
            )}
            {message &&
                <div style={{marginTop: '1em'}}>
                    <MessageBar
                        intent={messageTitle === 'Error' ? 'error' : 'info'}
                    >
                        <MessageBarBody>
                            <MessageBarTitle>{messageTitle}</MessageBarTitle>
                            {message}
                        </MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss"
                                    appearance="transparent"
                                    icon={<DismissRegular/>}
                                    onClick={() => {
                                        clearMessage();
                                    }}
                                />
                            }
                        >
                        </MessageBarActions>
                    </MessageBar>
                </div>
            }
            <div className="sf-results-col">
            {/* Merchant-configured hover styling for the product tile (cards + rows), emitted as a
                scoped stylesheet rule because `:hover` can't be an inline style. The transition is
                added here so the hover change animates smoothly. */}
            {cardHoverCss && <style>{`
                .card-container .card, .card-container .hoverable-card { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; }
                .card-container .card:hover, .card-container .hoverable-card:hover { ${cardHoverCss} }
            `}</style>}
            {/* Button hover from a referenced button style's `hover` part (see addToCartHoverCss). */}
            {addToCartHoverCss && <style>{`
                .add-to-cart-button, .sf-card-add-to-cart { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; }
                .add-to-cart-button:hover, .sf-card-add-to-cart:hover { ${addToCartHoverCss} }
            `}</style>}
            {/* card-container paddingBottom gives the last row's cards room for a configured
                drop shadow (searchPageConfig card.boxShadow); without it the shadow is clipped
                at the container's edge inside the scroll region. */}
            {viewMode === 'cards' ? (
                <div ref={cardGridRef} className="card-container" style={{
                    marginTop: '2em',
                    ...(searchConfig.cardGap != null ? {gap: searchConfig.cardGap} : {}),
                    ...containerStyle,
                    // Keep the last-row shadow room unless the merchant set their own box
                    // padding (then the `padding` shorthand covers the bottom). Note: emit
                    // NO paddingBottom key in that case — a value of `undefined` would make
                    // React clear the bottom the shorthand just set.
                    ...(containerStyle.padding != null ? {} : { paddingBottom: '2rem' }),
                }}>
                    {searchProducts.isFetching ? <CardSkeletonResults /> : searchProducts.data?.products.map((product, index) => (
                        <ProductCard
                            key={`result_${product.id ?? product.savedSearchId ?? product.bundleId}`}
                            product={product}
                            showAddToCart={canAddToCart}
                            cardStyle={cardStyle}
                            cardHoverStyle={cardHoverStyle}
                            nameStyle={nameStyle}
                            priceStyle={priceStyle}
                            imageStyle={imageStyle}
                            // Top-edge filters mirror the row view's manufacturer/category links:
                            // each applies its own dimension while keeping the other current filters.
                            onFilterManufacturer={() => navSearch(searchKeywords, searchSubCategory, searchCategory, searchSegment, product.manufacturer)}
                            onFilterCategory={() => navSearch(searchKeywords, product.subCategory, product.category, product.segment, searchManufacturer)}
                            // Attribute chips toggle their facet filter (mirrors the sidebar checkboxes),
                            // adopting the product's category as the scope when none is active yet.
                            onFilterAttribute={filterByAttribute}
                            activeAttributes={attributeFilters}
                            onFilterTag={toggleTag}
                            activeTags={tagFilters}
                            addToCartStyle={addToCartStyle}
                            source="search"
                            searchContext={searchContextForResult(index)}
                        />
                    ))}
                </div>
            ) : (
            <div className="search-results-list" style={{
                width: '100%',
                marginTop: '1em',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                // The container box wraps the rows too (mirrors card mode).
                ...containerStyle,
                // Give the last row's shadow / hover border room so it isn't clipped by
                // whatever sits flush below (paginator/footer) — unless the merchant set
                // their own box padding (then the `padding` shorthand covers the bottom).
                // Emit NO paddingBottom key in that case: `undefined` would make React
                // clear the bottom the shorthand just set.
                ...(containerStyle.padding != null ? {} : { paddingBottom: '2rem' }),
            }}>
                {searchProducts.isFetching ? <SkeletonResults /> : searchProducts.data?.products.map((product, index) => (
                    <div
                        className={'hoverable-card'}
                        ref={rowCardRef}
                        key={`result_${product.id ?? product.savedSearchId ?? product.bundleId}`}
                        onClick={() => {
                            // Carry the search origin (+ this result's rank) as router state so the
                            // destination page's product-view query can pass it along to the BFF.
                            const navOptions = { state: { source: 'search', searchContext: searchContextForResult(index) } };
                            if(product.savedSearchId){
                                nav(`/dynamicProducts/${product.savedSearchId}`, navOptions)
                            }
                            else if(product.bundleId){
                                nav(`/bundles/${product.bundleId}`, navOptions)
                            }
                            else{
                                nav(`/products/${product.id}`, navOptions)
                            }
                        }}
                    >
                        <div className="sr-image" style={Object.keys(imageStyle).length ? { ...imageStyle, overflow: 'hidden' } : undefined}>
                            <ProductImage src={product.image} style={{width: '100px', height: '100px'}} fit='contain' />
                            {(storefrontSettings.data?.enableImageHoverSwap ?? false) && (() => {
                                const hoverImg = (product.images ?? []).find(i => !!i && i !== product.image);
                                return hoverImg ? (
                                    <div className="sr-image-hover">
                                        <ProductImage src={hoverImg} style={{width: '100px', height: '100px'}} fit='contain' />
                                    </div>
                                ) : null;
                            })()}
                            {(storefrontSettings.data?.enableProductTags ?? false) && (product.tags ?? []).length > 0 && (
                                <div className="sr-tags-overlay">
                                    {(product.tags ?? []).filter(t => !!t && t.trim() !== '').slice(0, 2).map((tag) => {
                                        const c = tagColor(tag);
                                        const active = tagFilters.includes(tag);
                                        return (
                                            <span
                                                key={tag}
                                                className={`sr-tag-chip${active ? ' sr-tag-chip-active' : ''}`}
                                                style={{ backgroundColor: c.background, color: c.color }}
                                                title={active ? `Remove ${tag} filter` : `Filter by tag: ${tag}`}
                                                onClick={e => { e.stopPropagation(); toggleTag(tag); }}
                                            >
                                                <TagRegular className="sr-tag-icon" />{tag}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="sr-body">
                            <div className="sr-name" style={nameStyle}>
                                {product.name}
                            </div>
                            {product.attributes && product.attributes.length > 0 && (
                                <div className="sr-attrs">
                                    {product.attributes.map((attr, i) => {
                                        const active = attributeFilters.some(f => f.name === attr.key && f.value === attr.value);
                                        return (
                                            <span
                                                className={`sr-attr-chip${panelIsDark ? ' sr-attr-chip-dark' : ''}${active ? ' sr-attr-chip-active' : ''}`}
                                                key={`${attr.key}-${i}`}
                                                title={active ? `Remove ${attr.key} filter` : `Filter by ${attr.key}: ${attr.value}`}
                                                onClick={e => { e.stopPropagation(); filterByAttribute(attr.key, attr.value, product.category); }}
                                            >
                                                <span className="sr-attr-key">{attr.key}:</span> {attr.value}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="sr-actions">
                                <div className="sr-price" style={priceStyle}>
                                    {product.hidePrice
                                        ? <QuotePrice
                                            itemId={product.bundleId ?? (product.savedSearchId ? undefined : product.id)}
                                            itemType={product.bundleId ? 'bundle' : 'product'}
                                            itemName={product.name}
                                        />
                                        : canSeePricing && product.price &&
                                        <CurrencyFormat value={product.price} />
                                    }
                                    {/* Same qualifiers the card shows — the row layout is a separate render,
                                        so it has to opt in explicitly or listings silently lose them. */}
                                    <PriceQuantityNotice
                                        product={product}
                                        canSeePricing={canSeePricing}
                                        variant='short'
                                        className='sr-price-ceiling'
                                        style={{ margin: 0, fontSize: '0.75em', opacity: 0.8, fontWeight: 'normal' }}
                                    />
                                    <StockAvailabilityNotice
                                        product={product}
                                        className='sr-stock-low'
                                        style={{ margin: 0, fontSize: '0.75em', opacity: 0.8, fontWeight: 'normal' }}
                                    />
                                </div>
                                <FavoriteButton product={product} style={{ marginRight: 8 }} source="search" searchContext={searchContextForResult(index)} />
                                {canActuallyAddToCart && (
                                    <div
                                        onClick={e => e.stopPropagation()}
                                        className="card-add-to-cart sr-cart"
                                        style={{
                                            zIndex: 3,
                                            background: 'white',
                                            borderRadius: 13,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                            padding: 0,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <AddToCartButton product={product} buttonStyle={addToCartStyle} labelStyle={addToCartLabelStyle} source="search" searchContext={searchContextForResult(index)} />
                                    </div>
                                )}
                                {cartBlocked && (
                                    <BlockedCartButton />
                                )}
                            </div>
                            <div className="sr-meta">
                                {product.manufacturer && (
                                    <span style={{fontSize: 14, ...(Object.keys(manufacturerChipStyle).length ? { display: 'inline-block', ...manufacturerChipStyle } : {})}}>
                                        Manufacturer:
                                        <span
                                            style={{
                                                color: '#0078d4',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                marginLeft: 4
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setSearchPageNumber(1)
                                                setTimeout(() => {
                                                    navSearch(searchKeywords,searchSubCategory, searchCategory, searchSegment, product.manufacturer)
                                                }, 0);
                                            }}
                                        >
                                            {product.manufacturer}
                                        </span>
                                    </span>
                                )}
                                {getCategoryElement(product)}
                            </div>
                            {product.bundleId && <p style={{margin: 0}}>{L('localization_search_bundle_tag')}</p>}
                        </div>
                    </div>
                ))}
            </div>
            )}
            </div>
            </div>
            {/* Narrow layout: filters float as a modal over the results (toggled by the header button). */}
            {panelOn && isNarrow && filtersOpen && (
                <div className="sf-filter-modal-overlay" onClick={() => setFiltersOpen(false)}>
                    <div
                        className={`sf-filter-modal${panelIsDark ? ' sf-filter-modal-dark' : ''}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="sf-filter-modal-head">
                            <span>{L('localization_search_filters_heading')}</span>
                            <Button appearance="subtle" aria-label="Close filters"
                                className={panelIsDark ? 'sf-filter-toggle-dark' : undefined}
                                icon={<DismissRegular />} onClick={() => setFiltersOpen(false)} />
                        </div>
                        {renderPanelFilters()}
                    </div>
                </div>
            )}
        </div>
    );
}

const BlockedCartButton: React.FC = () => {
    const nav = useNavigate();
    const L = useLocalization();
    const [open, setOpen] = React.useState(false);
    const settings = useSelector((state: RootState) => state.cart);
    const { startNewCart, canStartNewCart } = useStartNewCart();

    return (
        <>
            <div
                className="card-add-to-cart"
                onClick={e => { e.stopPropagation(); setOpen(true); }}
                style={{
                    position: 'absolute',
                    right: 16,
                    bottom: 16,
                    zIndex: 3,
                    background: 'white',
                    borderRadius: 13,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                    padding: 0,
                    cursor: 'pointer',
                }}
            >
                <Tooltip content="Cannot add to current Prepared Cart" relationship="label">
                    <Button
                        appearance="subtle"
                        size="medium"
                        icon={<ProhibitedRegular style={{ color: '#d13438' }} />}
                        style={{ borderRadius: '50%', minWidth: 0, height: 30 }}
                    />
                </Tooltip>
            </div>
            <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
                <DialogSurface onClick={e => e.stopPropagation()}>
                    <DialogBody>
                        <DialogTitle>Cannot Add to Cart</DialogTitle>
                        <DialogContent>
                            Your current Prepared Cart does not allow adding additional items.{canStartNewCart ? ' You can view your current cart or start a new one.' : ''}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="primary"
                                onClick={() => {
                                    setOpen(false);
                                    nav(`/prepared-cart/${settings.selectedCartId}`);
                                }}
                            >
                                {L('localization_common_go_to_cart')}
                            </Button>
                            {canStartNewCart &&
                            <Button
                                icon={<NewFilled/>}
                                appearance="secondary"
                                onClick={async () => {
                                    setOpen(false);
                                    await startNewCart();
                                    nav('/cart');
                                }}
                            >
                                New Cart
                            </Button>}
                            <Button appearance="transparent" onClick={() => setOpen(false)}>{L('localization_common_cancel')}</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};

interface IAddToCartButtonProps {
    product: IProduct;
    onAdd?: () => void;
    promptOnAdd?: boolean;
    quantity?: number;
    // searchPageConfig.addToCartButton — merged into the visible add-to-cart Button.
    buttonStyle?: React.CSSProperties;
    // searchPageConfig.addToCartLabel — merged into the "Add to Cart" text label beside the button.
    // Lets a theme override the default white-pill/green-text look that doesn't fit every palette.
    labelStyle?: React.CSSProperties;
    // Bundle configuration selection — only used for regular-cart bundle adds (not prepared-cart).
    bundleSelection?: IBundleSelectionItem[];
    // Interest-signal context: the surface this button is rendered on (search/section/product/bundle/…)
    // and, when the surface is search, the active search criteria. Forwarded to the add-to-cart call
    // so the BFF can capture them onto the interest signal.
    source?: string;
    searchContext?: ISearchContext;
}

export const AddToCartButton: React.FC<IAddToCartButtonProps> = (props) => {
    const L = useLocalization();
    const settings = useSelector((state: RootState) => state.cart);
    const [addBundleToCart, { isLoading: isBundleLoading }] = useAddBundleToCartMutation();
    const [addBundleToPreparedCart, { isLoading: isPreparedCartBundleLoading }] = useAddBundleToPreparedCartMutation();
    const [addToCart, { isLoading: isProductLoading }] = useAddToCartMutation();
    const [addToPreparedCart, { isLoading: isPreparedCartProductLoading }] = useAddToPreparedCartMutation();
    const [addSavedToCart, { isLoading: isSavedLoading }] = useAddSavedSearchToCartMutation();
    const [added, setAdded] = React.useState<boolean>(false);
    const [cartDialog, setCartDialog] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const isLoading = isBundleLoading || isProductLoading || isSavedLoading || isPreparedCartBundleLoading || isPreparedCartProductLoading;

    // Derive directly — no intermediate state, no render lag
    const hasPreparedCart = !!settings.selectedCartId && !!getPreparedCart.data;
    const preparedCartAllowsAdd = hasPreparedCart && (getPreparedCart.data?.canAddAdditionalItems ?? false);
    const canAddToCart = !hasPreparedCart || preparedCartAllowsAdd;

    const showError = (msg: string) => {
        setErrorMessage(msg);
        setTimeout(() => setErrorMessage(''), 5000);
    };

    const checkMessages = (cart?: { messages?: string[] }): boolean => {
        if (cart?.messages && cart.messages.length > 0) {
            showError(cart.messages.join(' '));
            return true;
        }
        return false;
    };

    const checkError = (result: { error?: unknown }): boolean => {
        if (result.error) {
            const actionResponse = (result.error as any)?.data as IActionResponse | undefined;
            const msg = actionResponse?.error ?? actionResponse?.message ?? 'An error occurred. Please try again.';
            showError(msg);
            return true;
        }
        return false;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}
                onClick={async (e) => {
                    e.stopPropagation();
                    const activeCartId = settings.selectedCartId;
                    const usePreparedCart = !!activeCartId && (getPreparedCart.data?.canAddAdditionalItems ?? false);
                    let success = false;
                    setErrorMessage('');
                    try {
                        if (props.product.savedSearchId) {
                            if (usePreparedCart && props.product.id && props.product.catalog) {
                                const result = await addToPreparedCart({ catalog: props.product.catalog, cartId: activeCartId!, productId: props.product.id, quantity: props.quantity });
                                if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                            } else {
                                const result = await addSavedToCart({savedSearchId: props.product.savedSearchId, source: props.source, searchContext: props.searchContext});
                                if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                            }
                        } else if (props.product.bundleId) {
                            if (usePreparedCart) {
                                const result = await addBundleToPreparedCart({cartId: activeCartId!, bundleId: props.product.bundleId, quantity: props.quantity, selection: props.bundleSelection});
                                if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                            } else {
                                const result = await addBundleToCart({bundleId: props.product.bundleId, selection: props.bundleSelection, quantity: props.quantity, source: props.source, searchContext: props.searchContext});
                                if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                            }
                        } else {
                            if (usePreparedCart && props.product.id && props.product.catalog) {
                                const result = await addToPreparedCart({ catalog: props.product.catalog, cartId: activeCartId!, productId: props.product.id, quantity: props.quantity });
                                if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                            } else {
                                const result = await addToCart({productId: props.product.id, quantity: props.quantity, source: props.source, searchContext: props.searchContext});
                                if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                            }
                        }
                    } catch {}
                    if (success) {
                        props.onAdd?.();
                        if (props.promptOnAdd) {
                            setCartDialog(true);
                        } else {
                            setAdded(true);
                            setTimeout(() => setAdded(false), 1500);
                        }
                    }
                }}
            >
                {canAddToCart && (
                    <Button
                        appearance="primary"
                        className={'add-to-cart-button'}
                        size="medium"
                        icon={isLoading ? <Spinner size="tiny" /> : <CartRegular />}
                        aria-label="Add to cart"
                        style={{ zIndex: 2, borderRadius: '50%', minWidth: 0, height: 30, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', ...props.buttonStyle }}
                        disabled={isLoading}
                    />
                )}
                <span className="add-to-cart-label" style={{ background: 'rgba(255,255,255,0.95)', color: '#00744d', fontWeight: 600, fontSize: 14, borderRadius: 6, padding: '2px 10px', pointerEvents: 'none', transition: 'opacity 0.2s', ...props.labelStyle }}>
                    {added ? L('localization_common_added') : L('localization_common_add_to_cart')}
                </span>
                {errorMessage && (
                    <div
                        className="add-to-cart-error"
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: 'absolute',
                            left: 'calc(100% + 8px)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: '#fff',
                            border: '1px solid #f1707b',
                            borderRadius: 4,
                            padding: '6px 10px 6px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            zIndex: 10,
                            width: 400,
                            maxWidth: 400,
                            whiteSpace: 'normal' as const,
                        }}
                    >
                        <span className="add-to-cart-error-text" style={{ color: '#d13438', fontSize: 12, fontWeight: 600, flex: 1 }}>{errorMessage}</span>
                        <button
                            onClick={() => setErrorMessage('')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 14, padding: '0 2px', lineHeight: 1 }}
                            aria-label="Dismiss"
                        >✕</button>
                    </div>
                )}
            </div>
            {props.promptOnAdd && (
                <AddedToCartDialog open={cartDialog} onClose={setCartDialog} label={props.product.name} />
            )}
        </div>
    );
}

export default SearchPage;
